/**
 * 通用倒计时 Composable
 * 提供响应式的倒计时功能
 */

import { ref, computed, onUnmounted, type Ref, type ComputedRef } from 'vue'
import { Countdown } from '../core/utils/countdown'

export interface UseCountdownOptions {
  /** 倒计时秒数，默认 60 */
  seconds?: number
  /** 是否自动开始，默认 false */
  autoStart?: boolean
}

export interface UseCountdownReturn {
  /** 剩余秒数 */
  remaining: Ref<number>
  /** 是否正在运行 */
  isRunning: Ref<boolean>
  /** 是否可以开始 */
  canStart: ComputedRef<boolean>
  /** 格式化显示（如 "59s"） */
  formatted: ComputedRef<string>
  /** 开始倒计时 */
  start: (seconds?: number) => void
  /** 停止倒计时 */
  stop: () => void
  /** 重置倒计时 */
  reset: (seconds?: number) => void
  /** 重新开始 */
  restart: (seconds?: number) => void
}

/**
 * 通用倒计时 Composable
 * @param options 配置选项
 */
export function useCountdown(options: UseCountdownOptions = {}): UseCountdownReturn {
  const { seconds = 60, autoStart = false } = options

  // 响应式状态
  const remaining = ref(seconds)
  const isRunning = ref(false)

  // 创建倒计时实例
  const countdown = new Countdown({
    seconds,
    autoStart,
    onTick: (value) => {
      remaining.value = value
      isRunning.value = true
    },
    onEnd: () => {
      remaining.value = 0
      isRunning.value = false
    }
  })

  // 计算属性
  const canStart = computed(() => !isRunning.value)
  const formatted = computed(() => `${remaining.value}s`)

  // 方法
  const start = (secs?: number) => {
    countdown.start(secs)
    isRunning.value = true
  }

  const stop = () => {
    countdown.stop()
    isRunning.value = false
  }

  const reset = (secs?: number) => {
    countdown.reset(secs)
    remaining.value = secs ?? seconds
    isRunning.value = false
  }

  const restart = (secs?: number) => {
    countdown.restart(secs)
    isRunning.value = true
  }

  // 组件卸载时清理
  onUnmounted(() => {
    countdown.destroy()
  })

  return {
    remaining,
    isRunning,
    canStart,
    formatted,
    start,
    stop,
    reset,
    restart
  }
}
